import Papa from "papaparse";
import * as XLSX from "xlsx";

export interface ParsedLead {
  name: string;
  email?: string;
  phone?: string;
  extra_phones?: string[];
  stage?: string;
  notes?: string;
  metadata?: Record<string, any>;
}

export interface ColumnMapping {
  csvColumn: string;
  dbField: string | null;
  autoDetected: boolean;
}

export interface ParseResult {
  headers: string[];
  rows: Record<string, string>[];
  mappings: ColumnMapping[];
}

export interface ValidationResult {
  isValid: boolean;
  data: ParsedLead;
  errors: string[];
}

export interface StageInfo {
  csvValue: string;
  normalizedSlug: string;
  existingStageId?: string;
  existingStageName?: string;
  action: "use_existing" | "create_new" | "map_to";
  mapToStageId?: string;
  mapToStageName?: string;
}

export interface ExistingStage {
  id: string;
  name: string;
  slug: string;
  color: string;
  order_index: number;
  is_system: boolean;
}

// Aliases para mapeamento automático de colunas de leads
// Compatível com: Salesforce, HubSpot, Pipedrive, RD Station, Bling, etc.
const LEAD_COLUMN_ALIASES: Record<string, string[]> = {
  name: [
    "nome", "name", "lead_name", "contact_name", "contato", "cliente",
    "person_name", "full_name", "nome_completo", "first_name", "firstname",
    "nome_contato", "razao_social", "company_name", "organization"
  ],
  email: [
    "email", "e-mail", "email_address", "correio", "mail", "email_contato",
    "contact_email", "email_principal", "primary_email"
  ],
  phone: [
    "phone", "telefone", "tel", "celular", "mobile", "whatsapp", "fone",
    "phone_number", "telefone_celular", "telefone_principal", "cellphone",
    "mobile_phone", "contact_phone", "numero_telefone"
  ],
  phone_2: ["telefone_2", "phone_2", "celular_2", "whatsapp_2", "tel_2", "telefone_secundario"],
  phone_3: ["telefone_3", "phone_3", "celular_3", "whatsapp_3", "tel_3"],
  phone_4: ["telefone_4", "phone_4", "celular_4", "whatsapp_4", "tel_4"],
  stage: [
    "stage", "estagio", "status", "fase", "etapa", "pipeline_stage",
    "lifecycle_stage", "deal_stage", "situacao", "estado", "lead_status",
    "funil", "funnel_stage", "opportunity_stage"
  ],
  notes: [
    "notes", "notas", "observacao", "observacoes", "obs", "comentario",
    "comentarios", "descricao", "description", "anotacoes", "detalhes"
  ],
  company: [
    "company", "empresa", "organizacao", "org", "organization", "company_name",
    "nome_empresa", "razao_social", "account_name"
  ],
  position: [
    "position", "cargo", "job_title", "titulo", "funcao", "job", "role",
    "cargo_funcao", "title"
  ],
  source: [
    "source", "origem", "lead_source", "canal", "channel", "origem_lead",
    "acquisition_source", "marketing_source", "fonte"
  ],
  external_id: [
    "external_id", "id_externo", "id_crm", "ref", "id", "lead_id",
    "codigo", "code", "ref_externa", "id_sistema"
  ],
  razao_social: [
    "razao_social", "razao social", "company_name", "nome_empresa",
    "razaosocial", "corporate_name"
  ],
  cnpj: [
    "cnpj", "tax_id_pj"
  ],
  cpf: [
    "cpf", "documento_pessoal", "cpf_documento", "tax_id_pf"
  ],
  // Genérico: pode ser CPF ou CNPJ; será classificado pelo nº de dígitos
  documento: [
    "documento", "document", "tax_id", "cnpj_cpf", "cpf_cnpj"
  ],
  cidade_estado: [
    "cidade_estado", "cidade/estado", "cidade estado", "city_state",
    "localizacao", "localização", "location"
  ],
  cep: [
    "cep", "codigo_postal", "código postal", "postal_code", "zip", "zipcode",
    "zip_code"
  ],
  endereco: [
    "endereco", "endereço", "address", "logradouro", "rua", "street",
    "endereco_completo", "endereço completo", "endereco_cliente"
  ],
  numero: [
    "numero_endereco", "numero endereco", "n_endereco", "address_number",
    "street_number", "num_end", "no_endereco"
  ],
  complemento: [
    "complemento", "compl", "comp", "apto", "apartamento", "sala",
    "address_complement", "complement"
  ],
  bairro: [
    "bairro", "district", "neighborhood", "neighbourhood", "localidade_bairro"
  ],
  cidade: [
    "cidade", "city", "municipio", "município", "localidade"
  ],
  uf: [
    "uf", "estado", "state", "sigla_estado"
  ],
  birthday: [
    "birthday", "nascimento", "data_nascimento", "birth_date",
    "aniversario", "data_aniversario", "date_of_birth", "dob"
  ],
};

// Aliases para mapeamento inteligente de estágios
// Mapeia termos comuns de CRMs para estágios padrão
const STAGE_ALIASES: Record<string, string[]> = {
  novo: [
    "novo", "new", "lead", "entrada", "prospecto", "prospect", "inicial",
    "cold", "open", "aberto", "novo_lead", "new_lead", "incoming"
  ],
  qualificado: [
    "qualificado", "qualified", "mql", "sql", "interessado", "warm", "hot",
    "marketing_qualified", "sales_qualified", "engaged", "engajado"
  ],
  atendimento_humano: [
    "atendimento_humano", "human", "manual", "suporte", "support",
    "atendimento", "human_support", "escalado", "escalated"
  ],
  proposta: [
    "proposta", "proposal", "negociacao", "negotiation", "cotacao", "quote",
    "opportunity", "oportunidade", "em_negociacao", "negotiating",
    "proposal_sent", "proposta_enviada"
  ],
  ganho: [
    "ganho", "won", "closed_won", "closedwon", "fechado", "vendido", 
    "cliente", "customer", "converted", "convertido", "ativo",
    "closed", "sucesso", "success"
  ],
  perdido: [
    "perdido", "lost", "closed_lost", "closedlost", "cancelado", 
    "arquivado", "rejected", "churned", "desistiu", "nao_qualificado",
    "unqualified", "inativo"
  ],
};

// Cores disponíveis para novos estágios
const STAGE_COLORS = [
  "bg-gray-500", "bg-blue-500", "bg-green-500",
  "bg-yellow-500", "bg-orange-500", "bg-red-500",
  "bg-purple-500", "bg-pink-500", "bg-indigo-500", "bg-teal-500"
];

export function parseCSV(file: File): Promise<ParseResult> {
  const fileName = file.name.toLowerCase();
  const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

  if (isExcel) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(firstSheet, { raw: true, defval: "" });

          if (jsonData.length === 0) {
            return resolve({ headers: [], rows: [], mappings: [] });
          }

          const headers = Object.keys(jsonData[0]);
          const rows = jsonData.map((row) => {
            const stringRow: Record<string, string> = {};
            for (const key of headers) {
              const val = row[key];
              if (typeof val === "number") {
                // Avoid scientific notation for large numbers (e.g. phone numbers)
                stringRow[key] = Number.isFinite(val) ? BigInt(Math.round(val)).toString() : String(val);
              } else {
                stringRow[key] = String(val ?? "");
              }
            }
            return stringRow;
          });
          const mappings = detectColumnMappings(headers, rows);

          resolve({ headers, rows, mappings });
        } catch (err: any) {
          reject(new Error(`Erro ao processar Excel: ${err.message}`));
        }
      };
      reader.onerror = () => reject(new Error("Erro ao ler arquivo Excel"));
      reader.readAsArrayBuffer(file);
    });
  }

  // CSV flow — detect encoding (UTF-8 vs Windows-1252/Latin-1) automatically.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        let text: string;

        // 1) Try strict UTF-8.
        try {
          text = new TextDecoder("utf-8", { fatal: true }).decode(buffer);
        } catch {
          // 2) Fallback to Windows-1252 (superset of Latin-1, common in BR Excel exports).
          text = new TextDecoder("windows-1252").decode(buffer);
        }

        // 3) Even if strict UTF-8 succeeded, double-check for replacement chars
        //    that may have slipped through a non-fatal decode upstream.
        if (text.includes("\uFFFD")) {
          text = new TextDecoder("windows-1252").decode(buffer);
        }

        // 4) Strip BOM if present.
        if (text.charCodeAt(0) === 0xfeff) {
          text = text.slice(1);
        }

        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const headers = results.meta.fields || [];
            const rows = results.data as Record<string, string>[];
            const mappings = detectColumnMappings(headers, rows);
            resolve({ headers, rows, mappings });
          },
          error: (error: any) => {
            reject(new Error(`Erro ao processar CSV: ${error.message}`));
          },
        });
      } catch (err: any) {
        reject(new Error(`Erro ao decodificar CSV: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error("Erro ao ler arquivo CSV"));
    reader.readAsArrayBuffer(file);
  });
}

export function detectColumnMappings(
  headers: string[],
  rows: Record<string, string>[] = [],
): ColumnMapping[] {
  return headers.map((header) => {
    const ambiguousField = resolveAmbiguousNumberHeader(header, rows);
    if (ambiguousField) {
      return {
        csvColumn: header,
        dbField: ambiguousField,
        autoDetected: true,
      };
    }

    const normalizedHeader = normalizeString(header);
    let matchedField: string | null = null;

    for (const [dbField, aliases] of Object.entries(LEAD_COLUMN_ALIASES)) {
      if (aliases.some((alias) => normalizeString(alias) === normalizedHeader)) {
        matchedField = dbField;
        break;
      }
    }

    if (matchedField === "numero" && rows.length > 0) {
      const samples = sampleColumnValues(rows, header);
      if (samples.filter(looksLikePhoneValue).length > samples.length / 2) {
        matchedField = "phone";
      }
    }

    return {
      csvColumn: header,
      dbField: matchedField,
      autoDetected: matchedField !== null,
    };
  });
}

const AMBIGUOUS_NUMBER_HEADERS = new Set(["numero", "number", "num", "n", "no"]);

function sampleColumnValues(rows: Record<string, string>[], header: string): string[] {
  return rows
    .map((row) => row[header]?.trim())
    .filter((value): value is string => Boolean(value))
    .slice(0, 30);
}

function countDigits(value: string): number {
  return value.replace(/\D/g, "").length;
}

function looksLikePhoneValue(value: string): boolean {
  return countDigits(value) >= 10;
}

function looksLikeAddressNumberValue(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^s\/?n\.?$/i.test(trimmed)) return true;
  const digits = countDigits(trimmed);
  return digits > 0 && digits <= 6 && trimmed.length <= 12;
}

function resolveAmbiguousNumberHeader(
  header: string,
  rows: Record<string, string>[],
): "phone" | "numero" | null {
  const normalizedHeader = normalizeString(header);
  if (!AMBIGUOUS_NUMBER_HEADERS.has(normalizedHeader)) return null;

  const samples = sampleColumnValues(rows, header);
  if (samples.length === 0) return null;

  const phoneLike = samples.filter(looksLikePhoneValue).length;
  const addressLike = samples.filter(looksLikeAddressNumberValue).length;

  if (phoneLike > 0 && phoneLike >= addressLike) return "phone";
  if (addressLike > 0 && addressLike > phoneLike) return "numero";
  return null;
}

export function getLeadImportMappingWarnings(
  mappings: ColumnMapping[],
  rows: Record<string, string>[],
): string[] {
  const warnings: string[] = [];
  const hasPhoneMapping = mappings.some((mapping) => mapping.dbField === "phone");

  for (const mapping of mappings) {
    if (mapping.dbField !== "numero") continue;
    const samples = sampleColumnValues(rows, mapping.csvColumn);
    if (samples.length === 0) continue;
    const phoneLike = samples.filter(looksLikePhoneValue).length;
    if (phoneLike > samples.length / 2) {
      warnings.push(
        `A coluna "${mapping.csvColumn}" parece conter telefones, mas está mapeada como "Nº do endereço". Mapeie para "Telefone (contato)" para importar corretamente.`,
      );
    }
  }

  if (!hasPhoneMapping) {
    for (const mapping of mappings) {
      if (mapping.dbField) continue;
      const samples = sampleColumnValues(rows, mapping.csvColumn);
      if (samples.length === 0) continue;
      if (samples.filter(looksLikePhoneValue).length > samples.length / 2) {
        warnings.push(
          `A coluna "${mapping.csvColumn}" parece conter telefones, mas não está mapeada. Mapeie para "Telefone (contato)".`,
        );
      }
    }
  }

  return warnings;
}

export function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/[^a-z0-9]+/g, "_")     // Substitui caracteres especiais por _
    .replace(/^_+|_+$/g, "");        // Remove _ do início/fim
}

export function findStageByAlias(
  csvValue: string,
  existingStages: ExistingStage[]
): ExistingStage | null {
  const normalized = normalizeString(csvValue);
  const slug = generateSlug(csvValue);

  // Primeiro: match exato por slug
  const exactMatch = existingStages.find((s) => s.slug === slug);
  if (exactMatch) return exactMatch;

  // Segundo: match por slug normalizado
  const slugMatch = existingStages.find(
    (s) => normalizeString(s.slug) === normalized || normalizeString(s.name) === normalized
  );
  if (slugMatch) return slugMatch;

  // Terceiro: match por aliases conhecidos
  for (const [stageSlug, aliases] of Object.entries(STAGE_ALIASES)) {
    if (aliases.some((alias) => normalizeString(alias) === normalized)) {
      const aliasMatch = existingStages.find((s) => s.slug === stageSlug);
      if (aliasMatch) return aliasMatch;
    }
  }

  return null; // Não encontrou, precisa criar novo
}

export function extractUniqueStages(
  rows: Record<string, string>[],
  mappings: ColumnMapping[],
  existingStages: ExistingStage[]
): StageInfo[] {
  const stageMapping = mappings.find((m) => m.dbField === "stage");
  if (!stageMapping) return [];

  // Extrair valores únicos de estágio
  const uniqueValues = new Set<string>();
  for (const row of rows) {
    const value = row[stageMapping.csvColumn]?.trim();
    if (value) {
      uniqueValues.add(value);
    }
  }

  // Mapear cada valor único
  const stageInfos: StageInfo[] = [];
  const processedSlugs = new Set<string>();

  for (const csvValue of uniqueValues) {
    const slug = generateSlug(csvValue);
    
    // Evitar duplicatas de slug
    if (processedSlugs.has(slug)) continue;
    processedSlugs.add(slug);

    const existingStage = findStageByAlias(csvValue, existingStages);

    if (existingStage) {
      stageInfos.push({
        csvValue,
        normalizedSlug: existingStage.slug,
        existingStageId: existingStage.id,
        existingStageName: existingStage.name,
        action: "use_existing",
      });
    } else {
      stageInfos.push({
        csvValue,
        normalizedSlug: slug,
        action: "create_new",
      });
    }
  }

  return stageInfos;
}

export function getRandomColor(usedColors: string[]): string {
  const available = STAGE_COLORS.filter((c) => !usedColors.includes(c));
  if (available.length === 0) {
    return STAGE_COLORS[Math.floor(Math.random() * STAGE_COLORS.length)];
  }
  return available[Math.floor(Math.random() * available.length)];
}

export function transformRow(
  row: Record<string, string>,
  mappings: ColumnMapping[],
  stageInfos: StageInfo[]
): ValidationResult {
  const errors: string[] = [];
  const data: Partial<ParsedLead> = {};
  const metadata: Record<string, any> = {};

  for (const mapping of mappings) {
    if (!mapping.dbField) continue;

    const value = row[mapping.csvColumn]?.trim() || "";

    switch (mapping.dbField) {
      case "name":
        if (value) {
          data.name = value;
        }
        break;
      case "email":
        if (value) {
          // Validação básica de email
          if (value.includes("@")) {
            data.email = value;
          } else {
            errors.push("Email inválido");
          }
        }
        break;
      case "phone":
      case "phone_2":
      case "phone_3":
      case "phone_4":
        if (value) {
          let phoneValue = value;
          if (/[eE]\+/.test(phoneValue)) {
            try {
              phoneValue = BigInt(Math.round(parseFloat(phoneValue))).toString();
            } catch {
              phoneValue = parseFloat(phoneValue).toFixed(0);
            }
          }
          const cleaned = phoneValue.replace(/[^\d+]/g, "");
          if (!cleaned) break;
          if (mapping.dbField === "phone") data.phone = cleaned;
          else data.extra_phones = [...(data.extra_phones ?? []), cleaned];
        }
        break;
      case "stage":
        if (value) {
          // Encontrar o slug correto baseado no mapeamento de estágios
          const stageInfo = stageInfos.find(
            (s) => s.csvValue === value || generateSlug(value) === s.normalizedSlug
          );
          if (stageInfo) {
            if (stageInfo.action === "map_to" && stageInfo.mapToStageId) {
              // Buscar o slug do estágio mapeado
              data.stage = stageInfo.normalizedSlug;
            } else {
              data.stage = stageInfo.normalizedSlug;
            }
          } else {
            data.stage = "novo"; // Fallback
          }
        }
        break;
      case "notes":
        if (value) data.notes = value;
        break;
      case "company":
        if (value) metadata.company = value;
        break;
      case "position":
        if (value) metadata.position = value;
        break;
      case "source":
        if (value) metadata.source = value;
        break;
      case "external_id":
        if (value) metadata.external_id = value;
        break;
      case "razao_social":
        if (value) metadata.razao_social = value;
        break;
      case "cnpj":
        if (value) metadata.cnpj = value;
        break;
      case "cpf":
        if (value) metadata.cpf = value;
        break;
      case "documento": {
        // Heurística: 11 dígitos = CPF, caso contrário CNPJ
        const digits = value.replace(/\D+/g, "");
        if (digits.length === 11) metadata.cpf = value;
        else if (digits.length > 0) metadata.cnpj = value;
        break;
      }
      case "cidade_estado":
        if (value) metadata.cidade_estado = value;
        break;
      case "cep":
        if (value) metadata.cep = value;
        break;
      case "endereco":
        if (value) metadata.endereco = value;
        break;
      case "numero":
        if (value) metadata.numero = value;
        break;
      case "complemento":
        if (value) metadata.complemento = value;
        break;
      case "bairro":
        if (value) metadata.bairro = value;
        break;
      case "cidade":
        if (value) metadata.cidade = value;
        break;
      case "uf":
        if (value) metadata.uf = value.toUpperCase();
        break;
      case "birthday":
        if (value) metadata.birthday = value;
        break;
    }
  }

  // Adicionar metadata se houver
  if (Object.keys(metadata).length > 0) {
    data.metadata = metadata;
  }

  // Validações
  if (!data.name) {
    errors.push("Nome é obrigatório");
  }

  // Se não tem estágio definido, usar "novo"
  if (!data.stage) {
    data.stage = "novo";
  }

  return {
    isValid: errors.length === 0,
    data: data as ParsedLead,
    errors,
  };
}

export function getAvailableFields(): { value: string; label: string }[] {
  return [
    { value: "name", label: "Nome (obrigatório)" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Telefone (contato)" },
    { value: "phone_2", label: "Telefone 2 (mesmo lead)" },
    { value: "phone_3", label: "Telefone 3 (mesmo lead)" },
    { value: "phone_4", label: "Telefone 4 (mesmo lead)" },
    { value: "stage", label: "Estágio" },
    { value: "notes", label: "Notas/Observações" },
    { value: "company", label: "Empresa" },
    { value: "position", label: "Cargo" },
    { value: "source", label: "Origem" },
    { value: "external_id", label: "ID Externo" },
    { value: "razao_social", label: "Razão Social" },
    { value: "cnpj", label: "CNPJ" },
    { value: "cpf", label: "CPF" },
    { value: "cidade_estado", label: "Cidade/Estado" },
    { value: "cep", label: "CEP" },
    { value: "endereco", label: "Endereço (rua/logradouro)" },
    { value: "numero", label: "Nº do endereço" },
    { value: "complemento", label: "Complemento" },
    { value: "bairro", label: "Bairro" },
    { value: "cidade", label: "Cidade" },
    { value: "uf", label: "UF" },
    { value: "birthday", label: "Data de Nascimento" },
  ];
}

const TEMPLATE_HEADERS = [
  "nome",
  "email",
  "telefone",
  "estagio",
  "notas",
  "empresa",
  "cargo",
  "origem",
  "razao_social",
  "cnpj",
  "cpf",
  "cep",
  "endereco",
  "numero_endereco",
  "complemento",
  "bairro",
  "cidade",
  "uf",
  "cidade_estado",
  "data_nascimento",
  "telefone_2",
  "telefone_3",
  "telefone_4",
];

const TEMPLATE_EXAMPLES = [
  [
    "João Silva",
    "joao@email.com",
    "+55 11 99999-0001",
    "Novo",
    "Interessado em produto X",
    "Empresa ABC",
    "Gerente",
    "Website",
    "Empresa ABC LTDA",
    "12.345.678/0001-90",
    "",
    "01310-100",
    "Av. Paulista",
    "1000",
    "Sala 101",
    "Bela Vista",
    "São Paulo",
    "SP",
    "São Paulo/SP",
    "1990-05-15",
  ],
  [
    "Maria Santos",
    "maria@email.com",
    "+55 21 88888-0002",
    "Qualificado",
    "Reunião agendada",
    "Tech Solutions",
    "Diretora",
    "Indicação",
    "Tech Solutions SA",
    "98.765.432/0001-10",
    "",
    "22040-002",
    "Av. Atlântica",
    "500",
    "Apto 302",
    "Copacabana",
    "Rio de Janeiro",
    "RJ",
    "Rio de Janeiro/RJ",
    "1985-11-20",
  ],
  [
    "Pedro Costa",
    "pedro@email.com",
    "+55 31 77777-0003",
    "Proposta",
    "Aguardando retorno",
    "Startup XYZ",
    "CEO",
    "LinkedIn",
    "Startup XYZ ME",
    "",
    "123.456.789-00",
    "30140-071",
    "Rua da Bahia",
    "1200",
    "",
    "Centro",
    "Belo Horizonte",
    "MG",
    "Belo Horizonte/MG",
    "1992-03-08",
  ],
];

function padTemplateRow(row: string[], index: number): string[] {
  const padded = [...row];
  while (padded.length < TEMPLATE_HEADERS.length) padded.push("");
  if (index === 1) {
    const slot = TEMPLATE_HEADERS.indexOf("telefone_2");
    if (slot >= 0) padded[slot] = "11988887777";
  }
  return padded;
}

export function generateTemplateCSV(): string {
  const rows = [TEMPLATE_HEADERS, ...TEMPLATE_EXAMPLES].map(padTemplateRow);
  return rows.map((row) => row.map((cell) => `"${cell}"`).join(";")).join("\n");
}

export function generateTemplateXLSX(): Blob {
  const worksheet = XLSX.utils.aoa_to_sheet(
    [TEMPLATE_HEADERS, ...TEMPLATE_EXAMPLES].map(padTemplateRow),
  );
  
  // Define column widths
  worksheet["!cols"] = [
    { wch: 20 }, // nome
    { wch: 25 }, // email
    { wch: 20 }, // telefone
    { wch: 15 }, // estagio
    { wch: 30 }, // notas
    { wch: 20 }, // empresa
    { wch: 15 }, // cargo
    { wch: 15 }, // origem
    { wch: 25 }, // razao_social
    { wch: 22 }, // cnpj
    { wch: 18 }, // cpf
    { wch: 20 }, // cidade_estado
    { wch: 15 }, // data_nascimento
    { wch: 20 },
    { wch: 20 },
    { wch: 20 },
  ];
  
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
  
  const xlsxData = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
  return new Blob([xlsxData], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}
